import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import logic from "../logic";
import { conversationStore } from "@/lib/assistant/conversationStore";
import type { User } from "../logic/users/types";

interface AuthContextValue {
  isAuthenticated: boolean;
  loading: boolean;
  user: User | null;
  isAdmin: boolean;
  checkAuth: () => Promise<void>;
  /** Synchronously mark as authenticated — avoids the round-trip race that causes flicker on login→navigate. */
  setAuthenticatedUser: (userData: User) => void;
  clearAuth: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Stabilize checkAuth with useCallback to prevent unnecessary re-renders
  const checkAuth = useCallback(async () => {
    try {
      const userData = await logic.isUserLoggedIn();
      setIsAuthenticated(!!userData);
      setUser(userData);
    } catch (_error) {
      setIsAuthenticated(false);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // Initial auth check on mount
    let mounted = true;

    const initAuth = async () => {
      try {
        await checkAuth();
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initAuth();

    // Listen for auth changes (login/logout)
    const handleAuthChange = async () => {
      if (mounted) {
        await checkAuth();
      }
    };

    window.addEventListener("authChange", handleAuthChange);

    // Cleanup function to prevent memory leaks
    return () => {
      mounted = false;
      window.removeEventListener("authChange", handleAuthChange);
    };
  }, [checkAuth]);

  const setAuthenticatedUser = useCallback((userData: User) => {
    setIsAuthenticated(true);
    setUser(userData);
    setLoading(false);
  }, []);

  /** Logging out. The chat's conversation can hold health data: it leaves memory with the session. */
  const clearAuth = useCallback(() => {
    conversationStore.reset();
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider value={{ isAuthenticated, loading, user, isAdmin, checkAuth, setAuthenticatedUser, clearAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
